import { type PublishedState } from "@/constants";
import { type Locale, type SiteConfig, type UniqueIdentifier } from "@/types";
import {
  type FormBuilderConsentConfig,
  type FormBuilderFormEntity,
  type FormBuilderSubmitFormData,
} from "@/types/form-builder";
import { logAPICall } from "@/utils/client/apiLogger";

export interface SubmitFormResponseParams {
  formId: UniqueIdentifier;
  formData: FormBuilderSubmitFormData;
  siteConfig: SiteConfig;
  errorMessageFallback: string;
}

export async function submitFormResponse(params: SubmitFormResponseParams) {
  const {
    formId,
    formData,
    siteConfig: { siteId, locale, publishedState },
    errorMessageFallback,
  } = params;

  const url = `/caas-next/leadgen/v1/sites/${siteId}/forms/${formId}/responses`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": locale,
      "Published-State": publishedState,
    },
    body: JSON.stringify(formData),
  });
  logAPICall("POST", url, response.status);

  if (!response.ok) {
    const errorData = await response.json();
    logAPICall("POST", url, response.status, errorData.message);
    const responseError = new Error(errorData.message || errorMessageFallback);
    throw Object.assign(responseError, errorData);
  }

  return;
}

export interface CheckThankYouPageParams {
  thankYouPage: string;
  siteConfig: SiteConfig;
}

export async function checkThankYouPageExists(params: CheckThankYouPageParams) {
  const {
    thankYouPage,
    siteConfig: { locale, publishedState },
  } = params;

  return await fetch(thankYouPage, {
    method: "GET",
    headers: {
      "Accept-Language": locale,
      "Published-State": publishedState,
    },
  });
}

export interface FetchFormByIdParams {
  formId: UniqueIdentifier;
  siteId: UniqueIdentifier;
  locale: Locale;
  publishedState: PublishedState;
}

export async function fetchFormById(params: FetchFormByIdParams) {
  const { siteId, formId, locale, publishedState } = params;

  const url = `/caas-next/leadgen/v1/sites/${siteId}/forms/${formId}`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "Accept-Language": locale,
      "Published-State": publishedState,
    },
  });
  logAPICall("GET", url, response.status);

  if (response.ok) {
    return (await response.json()) as FormBuilderFormEntity;
  }

  const msg = `Failed to fetch form: ${response.status}`;
  logAPICall("GET", url, response.status, msg);
  return null;
}

export interface FetchConsentConfigParams {
  countryCode: string;
  siteConfig: SiteConfig;
}

export async function fetchConsentConfig(params: FetchConsentConfigParams) {
  const {
    countryCode,
    siteConfig: { locale, publishedState },
  } = params;

  const response = await fetch(
    `/caas-next/leadgen/v1/countries/${countryCode}/consent-config`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": locale,
        "Published-State": publishedState,
      },
    },
  );

  if (response.ok) {
    return (await response.json()) as FormBuilderConsentConfig;
  }

  return null;
}
